import { useEffect, useState } from "react";
import { Check, X, EyeOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMeta } from "@/lib/useMeta";
import { fetchPapersByStatus, fetchPaperJson, approvePaper, unpublishPaper, rejectPaper, deletePaper, updatePaperTags } from "@/lib/adminPapers";
import AdminPaperPreviewDialog from "@/components/AdminPaperPreviewDialog";
import AdminGate, { AdminNav } from "@/components/AdminGate";
import { useTaxonomies, refreshTaxonomies } from "@/lib/taxonomies";
import { describeTags } from "@/lib/taxonomy";

function PaperRow({ paper, onOpen }) {
  const taxonomies = useTaxonomies();
  const labels = describeTags(taxonomies, paper)
    .slice(0, 2)
    .map((t) => t.label);
  return (
    <button
      onClick={() => onOpen(paper)}
      className="w-full text-left paper-card p-3.5 flex items-center justify-between gap-3 hover:border-primary/50 transition-colors"
    >
      <div className="min-w-0">
        <p className="font-medium text-foreground truncate">{paper.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {paper.teacher_name || "নাম উল্লেখ করেননি"}
          {labels.map((l) => ` · ${l}`).join("")}
        </p>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {new Date(paper.created_at).toLocaleDateString("bn-BD")}
      </span>
    </button>
  );
}

function Moderation({ isAdmin }) {
  useMeta({ title: "অ্যাডমিন — মুমতাহিন গ্যালারি" });
  const [tab, setTab] = useState("pending");
  const [papers, setPapers] = useState(null);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  // অ্যাডমিন সবসময় সর্বশেষ ক্যাটাগরি দেখে (সাধারণ ভিজিটরের ক্যাশ ১ ঘণ্টার)
  useEffect(() => {
    refreshTaxonomies({ force: true });
  }, []);

  const load = () => {
    setPapers(null);
    setFailed(false);
    fetchPapersByStatus(tab).then(setPapers).catch(() => setFailed(true));
  };

  useEffect(load, [tab]);

  // তালিকায় paper_json নেই — খোলার সময় আনা হয়। আসার আগে অ্যাকশন বাটন বন্ধ থাকে।
  const openPaper = async (p) => {
    setActive({ ...p, paper_json: null });
    try {
      const json = await fetchPaperJson(p.id);
      setActive((cur) => (cur && cur.id === p.id ? { ...cur, paper_json: json } : cur));
    } catch (err) {
      console.error("Paper load failed:", err);
      setActive(null);
      alert("পেপারটা লোড করা যায়নি, আবার চেষ্টা করো।");
    }
  };

  const reject = (paper) => {
    const note = window.prompt("রিজেক্ট করলে পেপারের কনটেন্ট মুছে যাবে। কারণ লেখো (লেখক দেখতে পাবে, ফাঁকা রাখা যায়):");
    if (note === null) return; // বাতিল
    act(() => rejectPaper(paper.id, note));
  };

  const saveTags = async (paper, tags) => {
    try {
      const saved = await updatePaperTags(paper.id, tags);
      setActive((cur) => (cur && cur.id === paper.id ? { ...cur, term_values: saved } : cur));
      setPapers((list) => list?.map((p) => (p.id === paper.id ? { ...p, term_values: saved } : p)) ?? list);
    } catch (err) {
      console.error("Tag save failed:", err);
      alert(String(err?.message).includes("invalid_terms") ? "কোনো ট্যাগ এখন বৈধ নয় (ক্যাটাগরি বদলে গেছে?) — পেজ রিফ্রেশ করে আবার চেষ্টা করো।" : "ট্যাগ সেভ করা যায়নি, আবার চেষ্টা করো।");
    }
  };

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      setActive(null);
      load();
    } catch (err) {
      console.error("Admin action failed:", err);
      alert("কাজটা করা যায়নি, আবার চেষ্টা করো।");
    }
    setBusy(false);
  };

  return (
    <div className="container py-8">
      <AdminNav isAdmin={isAdmin} />

      <div className="flex gap-2 mb-5">
        {[
          ["pending", "রিভিউ বাকি"],
          ["approved", "পাবলিশড"],
          ["unpublished", "আনপাবলিশড"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
              tab === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {failed ? (
        <p className="text-muted-foreground text-sm">লোড করা যাচ্ছে না।</p>
      ) : papers === null ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="paper-card h-16 animate-pulse bg-muted" />
          ))}
        </div>
      ) : papers.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {{ pending: "রিভিউ বাকি কিছু নেই।", approved: "পাবলিশড কিছু নেই।", unpublished: "আনপাবলিশড কিছু নেই।" }[tab]}
        </p>
      ) : (
        <div className="space-y-2">
          {papers.map((p) => (
            <PaperRow key={p.id} paper={p} onOpen={openPaper} />
          ))}
        </div>
      )}

      <AdminPaperPreviewDialog
        paper={active}
        open={!!active}
        onOpenChange={(open) => !open && setActive(null)}
        onSaveTags={active && active.status !== "rejected" ? (tags) => saveTags(active, tags) : undefined}
        actions={
          active?.status === "pending" ? (
            <>
              <Button className="flex-1" disabled={busy || !active.paper_json} onClick={() => act(() => approvePaper(active.id))}>
                <Check className="w-4 h-4" /> অ্যাপ্রুভ করো
              </Button>
              <Button variant="outline" className="flex-1" disabled={busy} onClick={() => reject(active)}>
                <X className="w-4 h-4" /> রিজেক্ট করো
              </Button>
            </>
          ) : active?.status === "approved" ? (
            <>
              <Button variant="outline" className="flex-1" disabled={busy} onClick={() => act(() => unpublishPaper(active.id))}>
                <EyeOff className="w-4 h-4" /> আনপাবলিশ করো
              </Button>
              {isAdmin && (
                <Button variant="outline" className="flex-1 text-destructive" disabled={busy} onClick={() => act(() => deletePaper(active.id))}>
                  <Trash2 className="w-4 h-4" /> মুছে ফেলো
                </Button>
              )}
            </>
          ) : (
            <>
              <Button className="flex-1" disabled={busy || !active?.paper_json} onClick={() => act(() => approvePaper(active.id))}>
                <Check className="w-4 h-4" /> আবার পাবলিশ করো
              </Button>
              {isAdmin && (
                <Button variant="outline" className="flex-1 text-destructive" disabled={busy} onClick={() => act(() => deletePaper(active.id))}>
                  <Trash2 className="w-4 h-4" /> মুছে ফেলো
                </Button>
              )}
            </>
          )
        }
      />
    </div>
  );
}

export default function Admin() {
  return <AdminGate>{({ isAdmin }) => <Moderation isAdmin={isAdmin} />}</AdminGate>;
}
